import React from 'react';
import { Wifi, Ambulance, Box, AlertTriangle, Activity, Shield, Code } from 'lucide-react';

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
  highlight?: boolean;
}

const Feature: React.FC<FeatureProps> = ({ icon, title, description, color, highlight }) => {
  return (
    <div className={`bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow duration-300 ${
      highlight ? 'ring-2 ring-[#4CAF50] transform hover:-translate-y-1' : ''
    }`}>
      <div className={`inline-flex items-center justify-center p-3 rounded-lg mb-4 ${color}`}>
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const Features: React.FC = () => {
  const features = [
    {
      icon: <Wifi className="text-white" size={24} />,
      title: "Self-sufficient Internet",
      description: "Reliable and secure monitoring through a dedicated, separate Internet network connection ensuring uninterrupted operation.",
      color: "bg-[#2196F3]"
    },
    {
      icon: <Ambulance className="text-white" size={24} />,
      title: "Monitored Cooler System",
      description: "Advanced temperature-controlled environment designed specifically for safe organ transportation, maintaining optimal conditions throughout the journey.",
      color: "bg-[#4CAF50]",
      highlight: true
    },
    {
      icon: <Box className="text-white" size={24} />,
      title: "Surgical Cart Solutions",
      description: "Advanced monitoring systems for surgical carts enabling immediate response to temperature and humidity variations.",
      color: "bg-[#2196F3]"
    },
    {
      icon: <AlertTriangle className="text-white" size={24} />,
      title: "Real-time Alerts",
      description: "Instant notifications for environmental condition changes, ensuring quick response to maintain optimal conditions for safe organ preservation.",
      color: "bg-[#4CAF50]"
    },
    {
      icon: <Activity className="text-white" size={24} />,
      title: "Environmental Control",
      description: "Specialized monitoring systems for operating rooms and sterile processing departments providing real-time environmental data.",
      color: "bg-[#2196F3]"
    },
    {
      icon: <Code className="text-white" size={24} />,
      title: "RESTful API Integration",
      description: "Secure REST API enabling seamless integration with your internal systems. Access real-time data, manage devices, and automate workflows.",
      color: "bg-[#4CAF50]"
    }
  ];

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Comprehensive Features</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Our intelligent environmental monitoring solutions ensure optimal conditions
            for healthcare facilities and organ transportation while maintaining the highest standards of safety and efficiency.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <Feature
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              color={feature.color}
              highlight={feature.highlight}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;